/**
 * @module utils/load-script
 */
import isNil from "lodash/isNil";

/**
 * @let cache
 * @description object containing a hash of all loaded scripts
 */
let cache = {};

/**
 * @method checkInCache
 * @description whether script exists in cache already
 * @param {String} url - a url string used as a key to check in the `cache` object
 * @returns {Boolean}
 */
function checkInCache(url) {
  return !isNil(cache[url]);
}

/**
 * @method promiseHandler
 * @param {String} url - url to an external script
 * @param {Object} attributes - an object containing a hash of of attributes to be added to the script element
 * @param {Function} resolve - method called when resolved
 * @param {Function} reject - method called when rejected
 */
export function promiseHandler(url, attributes, resolve, reject) {
  const script = document.createElement("script");
  let ready = false;

  if (checkInCache(url)) {
    // if url was already called then resolve with the cached value
    resolve(cache[url]);
  } else {
    // temporarily set cache to true so subsequent calls don't trigger another ajax request
    cache[url] = true;
    script.type = "text/javascript";
    script.src = url;
    script.async = true;
    script.onload = script.onreadystatechange = function _handleScriptReadyState() {
      if (!ready && (!this.readyState || this.readyState === "complete")) {
        ready = true;
        // store record of promise so js file is never loaded twice
        cache[url] = this;
        resolve(this);
      }
    };
    script.onerror = script.onabort = reject;

    if (!isNil(attributes)) {
      for (let attr in attributes) {
        script.setAttribute(attr, attributes[attr]);
      }
    }

    let ref = document.querySelector("script");
    if (!ref) {
      ref = document.body;
      ref.appendChild(script, ref);
    } else {
      ref.parentNode.insertBefore(script, ref);
    }
  }
}

/**
 * @method loadScript
 * @description makes a request to asynchronously load javascript, accepts a string or an array of strings
 * @param {String} url - url to an external script
 * @param {Object} attributes - an object containing a hash of of attributes to be added to the script element
 * @param {Function} promiseMethod - method called once the promise has resolved or rejected
 * @returns {Promise}
 */
export default function loadScript(url, attributes = null, promiseMethod = promiseHandler) {
  // if url is an array then call itself recursively but this time passing a string
  if (Array.isArray(url)) {
    const scripts = [];
    url.map(item => {
      scripts.push(loadScript(item, attributes));
    });
    return Promise.all(scripts);
  }

  return new Promise(promiseMethod.bind(this, url, attributes));
}

